import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import PropConfigFilter from './PropConfigFilter';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';

const PropConfigList = () => {
    let navigate = useNavigate();
    const dispatch = useDispatch();
    const propConfigList = useSelector(
        (state) => state.propconfig.propconfigList,
    );


    const [deleteRecord, setDeleteRecord] = useState(null); // Store record for deletion
    const propConfig = useSelector((state) => state.propconfig.propconfig);
    const filters = useSelector((state) => state.propconfig.filters);
    
    const defaultParams = useMemo(() => ({
        ...filters,
        pageno: 1,
        pagesize: 50,
        sort: 'createdOn',
        sortOrder: -1,
        status: "Active"
    }), [filters]);

    const [formValues, setFormValues] = useState(defaultParams);
    
    const handleReset = () => {
        dispatch(appActions.PROPCONFIG_GET_ALL_REQUEST(defaultParams));
    };

    const handleRefresh = useCallback(() => {
        dispatch(appActions.PROPCONFIG_GET_ALL_REQUEST(defaultParams));
    }, [dispatch, defaultParams]);

    useEffect(() => {
        if (propConfig?.action === 'delete') {
            handleRefresh();
        }
    }, [propConfig, handleRefresh]);

    const handleSearch = useCallback(
        (params) => {
            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };
    
            dispatch(appActions.PROPCONFIG_GET_ALL_REQUEST(searchParams));
        },
        [dispatch, defaultParams] 
    );

    useEffect(() => {
        handleSearch(defaultParams);
    }, []); // Empty dependency array ensures it runs only on mount
    
    const linkToNew = useCallback(
        () => navigate('/ui/prop-confg/detail', { replace: false }),
        [navigate]
    );

    const handleViewRedirect = useCallback((record) => {
        const data = propConfigList?.data?.find((t) => t.id === record.id);
        navigate('/ui/prop-confg/detail', { state: { data, isView: true } });
    }, [propConfigList, navigate]);

    const handleEditRedirect = useCallback((record) => {
        const data = propConfigList?.data?.find((t) => t.id === record.id);
        navigate('/ui/prop-confg/detail', { state: { data, isEdit: true } });
    }, [propConfigList, navigate]);

    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.PROPCONFIG_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex">Configuration </h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='mr-auto tp' data-pr-tooltip="Refresh" onClick={handleRefresh} />
                <Button label="Add New" icon="pi pi-plus" className="p-button-outlined tp" data-pr-tooltip="Add New Configuration" onClick={linkToNew} />
            </div>
        );
    };

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                    <i className="pi pi-eye text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                    <i className="pi pi-pencil text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                    <i className="pi pi-times-circle text-base text-danger"/>
                </span>
            </div>
        ),
        [handleViewRedirect, handleEditRedirect, handleDelete],
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );

    const propValueTemplate = row => {
        const { propValue } = row
        const logTestStr = JSON.stringify(propValue)
        return logTestStr
    }

    const columns = [
        { field: 'propCode', header: 'Property Code', sortable: true, width : '220px', maxWidth: '220px' },
        { field: 'propValue', header: 'Property Value', sortable: true,  template: propValueTemplate, maxWidth : '250px' },
        { field: 'propGroup', header: 'Property Group', sortable: true, width : '160px' },
        { field: 'status', header: 'Status', sortable: true, width: '90px' },
        { field: 'createdOn', header: 'Created On', sortable: true, width: '140px', columnType : 'datetime'  },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, width : '150px', columnType : 'datetime' },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true, width : '150px' },
    ];
    
    const renderList = () => {
        return (
            <>
                <div className="grid px-4 align-items-center my-2"> {renderHeader()} </div>
                <div className="bg-white mx-3 pv-20 ph-20 br-6 shadow-white">
                    <PropConfigFilter
                        handleSearch={handleSearch}
                        handleReset={handleReset}
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={propConfigList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='configuration'
                        isLazy={true}
                        showGenerateXmlBtn={false}
                    />
                </div>
            </>
        );
    };
    return <div className="form_height">
         {renderList()}
         {deleteRecord && (
             <ConfirmationDialog
                 message={`Are you sure you want to delete the configuration : ${deleteRecord.propCode}?`}
                 onAccept={handleDeleteConfirm}
                 onReject={() => setDeleteRecord(null)}
             />
         )}
    </div>;
};

export default PropConfigList;
